import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Navbar } from '../components/Navbar';
import { toast } from 'sonner';
import { 
  Rocket, ArrowRight, Calendar, MapPin, Users, Zap, 
  Cpu, Brain, Code2, Terminal, Network, Layers, 
  CheckCircle2, Target, GitBranch, Database,
  Lightbulb, Handshake, UserPlus, Mail, ChevronRight
} from 'lucide-react';

export default function LandingPage() {
  const [email, setEmail] = useState('');

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      toast.success('Welcome to the network. We\'ll be in touch.');
      setEmail('');
    }
  };

  const upcomingEvents = [
    {
      title: 'AI Systems Architecture',
      tags: ['AI', 'Systems Design'],
      description: 'Build production-grade AI systems. Focus on scalable architectures, model deployment, and real-world implementation challenges.',
      date: 'Every Thursday',
      location: 'Pearl District Tech Hub',
      attendees: '30',
      color: 'indigo'
    },
    {
      title: 'ML Engineering Deep Dive',
      tags: ['ML', 'Engineering'],
      description: 'Advanced machine learning engineering. From data pipelines to model optimization—real problems, real solutions.',
      date: 'Every Saturday',
      location: 'Downtown Innovation Lab',
      attendees: '25',
      color: 'emerald'
    },
    {
      title: 'Systems & Infrastructure',
      tags: ['Backend', 'DevOps'],
      description: 'Build bulletproof infrastructure. Distributed systems, cloud architecture, and engineering excellence.',
      date: 'Every Tuesday',
      location: 'Eastside Tech Campus',
      attendees: '35',
      color: 'slate'
    }
  ];

  const focusAreas = [
    { name: 'Artificial Intelligence', icon: Brain },
    { name: 'Machine Learning', icon: Cpu },
    { name: 'Software Engineering', icon: Code2 },
    { name: 'Systems Architecture', icon: Layers },
    { name: 'Data Engineering', icon: Database },
    { name: 'DevOps & Infrastructure', icon: Terminal }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-slate-950">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(99,102,241,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(16,185,129,0.1),transparent_50%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(0,0,0,0.1)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,0,0,0.1)_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 rounded-full text-sm font-medium mb-8">
              <Zap className="w-4 h-4" />
              Weekly Build Sessions · Portland, OR
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Portland's{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-emerald-400">Build</span>{' '}
              Community
            </h1>
            
            <p className="text-xl text-slate-300 max-w-2xl mb-4 leading-relaxed">
              A curated network of technology professionals pushing the boundaries of what's possible.
            </p>
            <p className="text-lg text-slate-400 max-w-2xl mb-10">
              AI. Machine Learning. Engineering. <span className="text-slate-300">For those who build the future.</span>
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Link to="/events">
                <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100 text-base px-8 font-semibold" data-testid="hero-join-event-btn">
                  Join This Week
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/register">
                <Button size="lg" variant="outline" className="text-base px-8 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white" data-testid="hero-apply-btn">
                  Apply for Access
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-slate-900 border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-white">Weekly</div>
              <div className="text-slate-400 text-sm mt-1">Build Sessions</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">4000+</div>
              <div className="text-slate-400 text-sm mt-1">Active Engineers</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">AI/ML</div>
              <div className="text-slate-400 text-sm mt-1">Primary Focus</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">Senior+</div>
              <div className="text-slate-400 text-sm mt-1">Experience Level</div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              A Weekend Experience Like No Other
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              PDX Hacks is a focused network of senior engineers, ML practitioners, and technical leaders 
              who are serious about advancing the state of technology. We build. We ship. We challenge the status quo.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-8 rounded-2xl bg-slate-50 border border-slate-200">
              <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center mb-6">
                <Target className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">
                Focused Excellence
              </h3>
              <p className="text-slate-600 leading-relaxed">
                No intro tutorials. No beginner content. Every session is designed for professionals 
                ready to tackle complex technical challenges.
              </p>
            </div>

            <div className="p-8 rounded-2xl bg-slate-50 border border-slate-200">
              <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center mb-6">
                <Network className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">
                Curated Network
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Connect with engineers from top companies and startups. A selective community 
                where quality conversations happen naturally.
              </p>
            </div>

            <div className="p-8 rounded-2xl bg-slate-50 border border-slate-200">
              <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center mb-6">
                <GitBranch className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">
                Real Output
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Every session produces working code, prototypes, or technical artifacts. 
                We measure success by what gets shipped.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Focus Areas */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Technical Focus Areas
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Deep expertise in the technologies shaping the future
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {focusAreas.map((area, index) => (
              <div 
                key={index} 
                className="flex flex-col items-center p-6 bg-slate-900/50 border border-slate-800 rounded-xl hover:border-indigo-500/50 transition-colors"
              >
                <area.icon className="w-8 h-8 text-indigo-400 mb-3" />
                <span className="text-sm font-medium text-slate-300 text-center">{area.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Weekly Sessions */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Weekly Build Sessions
              </h2>
              <p className="text-slate-600 max-w-2xl text-lg">
                Structured sessions focused on shipping real work
              </p>
            </div>
            <Link to="/events" className="mt-4 md:mt-0">
              <Button variant="outline" className="gap-2">
                View Full Schedule
                <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {upcomingEvents.map((event, index) => (
              <div key={index} className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-xl hover:border-slate-300 transition-all duration-300">
                <div className={`h-1 ${event.color === 'indigo' ? 'bg-indigo-600' : event.color === 'emerald' ? 'bg-emerald-600' : 'bg-slate-600'}`} />
                <div className="p-6">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {event.tags.map((tag, i) => (
                      <Badge key={i} variant="secondary" className="text-xs font-medium bg-slate-100">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-indigo-600 transition-colors">
                    {event.title}
                  </h3>
                  <p className="text-slate-600 text-sm mb-5 leading-relaxed">
                    {event.description}
                  </p>
                  <div className="space-y-2 text-sm text-slate-500 mb-5">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {event.date}
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {event.location}
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      {event.attendees} engineers
                    </div>
                  </div>
                  <Link to="/events">
                    <Button variant="outline" className="w-full group-hover:bg-slate-900 group-hover:text-white group-hover:border-slate-900 transition-colors">
                      Reserve Spot
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Built for Technical Excellence
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                PDX Hacks is designed for technology professionals who refuse to accept the status quo. 
                If you're serious about pushing boundaries and building what's next, you belong here.
              </p>
              
              <div className="space-y-4">
                {[
                  'Senior Engineers & Tech Leads shipping production systems',
                  'ML Engineers & Data Scientists building intelligent systems',
                  'Architects designing scalable, resilient infrastructure',
                  'Technical Founders turning vision into working products',
                  'Open Source Contributors advancing the ecosystem'
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-10">
                <Link to="/register">
                  <Button size="lg" className="bg-slate-900 hover:bg-slate-800">
                    Apply to Join
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-indigo-500/10 to-emerald-500/10 rounded-3xl blur-2xl" />
              <div className="relative bg-slate-900 rounded-2xl p-8 text-white">
                <div className="font-mono text-sm text-slate-400 mb-6">// What we build</div>
                <div className="space-y-4 font-mono text-sm">
                  <div><span className="text-indigo-400">const</span> <span className="text-emerald-400">focus</span> = [</div>
                  <div className="pl-4 text-slate-300">"Production AI Systems",</div>
                  <div className="pl-4 text-slate-300">"ML Infrastructure",</div>
                  <div className="pl-4 text-slate-300">"Distributed Systems",</div>
                  <div className="pl-4 text-slate-300">"Developer Tools",</div>
                  <div className="pl-4 text-slate-300">"Open Source"</div>
                  <div>];</div>
                  <div className="mt-6"><span className="text-indigo-400">const</span> <span className="text-emerald-400">outcome</span> = <span className="text-amber-400">"shipped"</span>;</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Get Involved Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Ways to Engage
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg">
              Multiple pathways into Portland's technical community
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/events" className="group">
              <div className="p-6 rounded-xl border-2 border-slate-200 hover:border-slate-900 transition-colors h-full">
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-slate-900 transition-colors">
                  <Calendar className="w-6 h-6 text-slate-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Attend Sessions</h3>
                <p className="text-slate-600 text-sm">Join weekly build sessions and ship alongside other senior engineers.</p>
              </div>
            </Link>

            <Link to="/projects/create" className="group">
              <div className="p-6 rounded-xl border-2 border-slate-200 hover:border-slate-900 transition-colors h-full">
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-slate-900 transition-colors">
                  <Lightbulb className="w-6 h-6 text-slate-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Lead a Project</h3>
                <p className="text-slate-600 text-sm">Propose technical challenges and build with a team of experts.</p>
              </div>
            </Link>

            <Link to="/contact" className="group">
              <div className="p-6 rounded-xl border-2 border-slate-200 hover:border-slate-900 transition-colors h-full">
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-slate-900 transition-colors">
                  <Handshake className="w-6 h-6 text-slate-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Partner With Us</h3>
                <p className="text-slate-600 text-sm">Connect your company with Portland's top technical talent.</p>
              </div>
            </Link>

            <Link to="/mentors" className="group">
              <div className="p-6 rounded-xl border-2 border-slate-200 hover:border-slate-900 transition-colors h-full">
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-slate-900 transition-colors">
                  <UserPlus className="w-6 h-6 text-slate-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Share Expertise</h3>
                <p className="text-slate-600 text-sm">Lead technical deep-dives and mentor the next generation.</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Build?
          </h2>
          <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
            Join Portland's most focused technical community. Weekly sessions. Real projects. Serious engineers.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100 px-8 font-semibold">
                Apply for Membership
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/events">
              <Button size="lg" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white px-8">
                View This Week's Sessions
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-slate-900 border-t border-slate-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-xl font-bold text-white mb-3">
            Stay in the loop
          </h3>
          <p className="text-slate-400 mb-6">Technical updates, session announcements, and community highlights.</p>
          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <Input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              data-testid="newsletter-email-input"
            />
            <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 whitespace-nowrap" data-testid="newsletter-subscribe-btn">
              <Mail className="w-4 h-4 mr-2" />
              Subscribe
            </Button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-12 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                <Rocket className="w-4 h-4 text-slate-900" />
              </div>
              <span className="text-white font-bold">PDX<span className="text-indigo-400">Hacks</span></span>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <Link to="/events" className="hover:text-white transition-colors">Sessions</Link>
              <Link to="/projects" className="hover:text-white transition-colors">Projects</Link>
              <Link to="/forum" className="hover:text-white transition-colors">Forum</Link>
              <Link to="/mentors" className="hover:text-white transition-colors">Mentors</Link>
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
            </div>
            
            <p className="text-sm text-slate-500">
              Portland, Oregon
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
